import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  Search,
  MoreVert,
  PersonOutlined,
  Logout as LogoutIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { logout } from '../config/auth';
import { ConversationWithUser, UserData } from '../types';
import { isOnline, formatConversationTime } from '../utils/format';
import { onlineBadgeSx, borderlessInputSx } from '../utils/styles';

interface ChatListProps {
  conversations: ConversationWithUser[];
  selectedConversation: ConversationWithUser | null;
  searchResults: UserData[] | null;
  onSearch: (term: string) => void;
  onSelectConversation: (conv: ConversationWithUser) => void;
  onSelectUser: (user: UserData) => void;
  loading: boolean;
}

const ChatList: React.FC<ChatListProps> = ({
  conversations,
  selectedConversation,
  searchResults,
  onSearch,
  onSelectConversation,
  onSelectUser,
  loading,
}) => {
  const navigate = useNavigate();
  const { userData } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/');
  };

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={userData?.avatar}
            sx={{
              width: 40,
              height: 40,
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #7c4dff, #ff4081)',
              boxShadow: '0 0 12px rgba(124,77,255,0.3)',
            }}
            onClick={() => navigate('/profile')}
          >
            {userData?.name?.[0]?.toUpperCase()}
          </Avatar>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #e8e8f0, #7c4dff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Chats
          </Typography>
        </Box>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ color: '#8888a8' }}
        >
          <MoreVert />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          slotProps={{
            paper: {
              sx: {
                bgcolor: '#1a1a2e',
                border: '1px solid rgba(255,255,255,0.06)',
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              navigate('/profile');
            }}
          >
            <PersonOutlined sx={{ mr: 1, color: '#8888a8' }} fontSize="small" />
            Edit Profile
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 1, color: '#ff4081' }} fontSize="small" />
            Logout
          </MenuItem>
        </Menu>
      </Box>

      {/* Search */}
      <Box sx={{ px: 2, pb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search users by username..."
          value={searchTerm}
          onChange={handleSearchChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#5555a0', fontSize: 20 }} />
                </InputAdornment>
              ),
            },
          }}
          sx={borderlessInputSx}
        />
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          [...Array(6)].map((_, i) => (
            <Box
              key={i}
              sx={{ display: 'flex', alignItems: 'center', p: 2, gap: 2 }}
            >
              <Skeleton
                variant="circular"
                width={48}
                height={48}
                sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton
                  width="60%"
                  height={20}
                  sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}
                />
                <Skeleton
                  width="80%"
                  height={16}
                  sx={{ mt: 0.5, bgcolor: 'rgba(255,255,255,0.03)' }}
                />
              </Box>
            </Box>
          ))
        ) : searchResults !== null ? (
          searchResults.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: '#5555a0' }} variant="body2">
                No users found
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  py: 1,
                  display: 'block',
                  color: '#7c4dff',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Search Results
              </Typography>
              {searchResults.map((user) => (
                <ListItemButton
                  key={user.id}
                  onClick={() => {
                    onSelectUser(user);
                    setSearchTerm('');
                  }}
                  sx={{
                    '&:hover': { bgcolor: 'rgba(124,77,255,0.06)' },
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      variant="dot"
                      invisible={!isOnline(user.lastSeen)}
                      sx={onlineBadgeSx}
                    >
                      <Avatar
                        src={user.avatar}
                        sx={{
                          background:
                            'linear-gradient(135deg, #5c35cc, #7c4dff)',
                        }}
                      >
                        {(user.name || user.username)?.[0]?.toUpperCase()}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.name || user.username}
                    secondary={user.bio}
                    slotProps={{
                      primary: { sx: { fontWeight: 500, color: '#e8e8f0' } },
                      secondary: { noWrap: true, sx: { color: '#6868a0' } },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          )
        ) : conversations.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              mt: 4,
            }}
          >
            <ChatIcon sx={{ fontSize: 56, color: '#2a2a50' }} />
            <Typography variant="subtitle1" sx={{ color: '#5555a0' }}>
              No conversations yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#4444a0' }}>
              Search for a user to start chatting
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {conversations.map((conv) => (
              <ListItemButton
                key={conv.id}
                selected={selectedConversation?.id === conv.id}
                onClick={() => onSelectConversation(conv)}
                sx={{
                  py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(124,77,255,0.08)',
                    borderLeft: '2px solid #7c4dff',
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: 'rgba(124,77,255,0.12)',
                  },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                }}
              >
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    variant="dot"
                    invisible={!isOnline(conv.otherUser.lastSeen)}
                    sx={onlineBadgeSx}
                  >
                    <Avatar
                      src={conv.otherUser.avatar}
                      sx={{
                        background:
                          'linear-gradient(135deg, #5c35cc, #7c4dff)',
                      }}
                    >
                      {conv.otherUser.name?.[0]?.toUpperCase()}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={conv.otherUser.name || conv.otherUser.username}
                  secondary={conv.lastMessage?.text || 'Start a conversation'}
                  slotProps={{
                    primary: {
                      sx: { fontWeight: 500, color: '#e8e8f0' },
                      noWrap: true,
                    },
                    secondary: {
                      noWrap: true,
                      sx: { color: '#6868a0' },
                    },
                  }}
                />
                {conv.lastMessage?.createdAt && (
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 1,
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                      mt: 0.5,
                      color: '#5555a0',
                    }}
                  >
                    {formatConversationTime(conv.lastMessage.createdAt)}
                  </Typography>
                )}
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </>
  );
};

export default ChatList;
